import React, { type FunctionComponent, useMemo } from 'react'
import { type ApolloError } from '@apollo/client'
import {
  type GridColumns,
  type GridColumn,
  type GridDatum,
  GridList,
  withContainerSize,
} from '@retailer-platform/shared-components'
import { FormattedDate } from 'react-intl'
import { EmptyState, EmptyStateArtwork, EmptyStateTitle } from '@instacart/ids-customers'
import { ErrorPage } from '@retailer-platform/dashboard/gin-and-tonic'
import { type CellProps } from 'react-table-next'
import { type ReportTypeEnum } from '../../../../api'
import { REPORT_TYPES_BASED_ON_STORE_GROUPS } from '../../utils/ReportsNormalizedReportsList.utils'
import reportsEmptyStateImg from '../../../../assets/reports-empty-state-illustration.png'
import { ReportsNormalizedReportsListDefaultCell } from '../default-cell/ReportsNormalizedReportsListDefaultCell'
import { ReportsNormalizedReportsListDefaultHeader } from '../default-header/ReportsNormalizedReportsListDefaultHeader'
import { type ReportsNormalizedReportsDatum } from '../../utils/ReportsNormalizedReportsList.types'
import { ReportsNormalizedReportsListDownloadCell } from '../download-cell/ReportsNormalizedReportsListDownloadCell'
import {
  useDomainMessage,
  useDomainMessages,
  type DomainMessages,
} from '../../../../utils/domain/intl'

const ExpandableGridList = withContainerSize(GridList)

interface ReportsNormalizedReportsGridListProps {
  reports: ReportsNormalizedReportsDatum[]
  loading: boolean
  canLoadMore?: boolean
  loadMore: () => void
  error?: ApolloError
  refetch: () => void
  isHeavyQuery: boolean
  reportType?: ReportTypeEnum
}

const getBaseColumns = (
  isStoreGroupReport: boolean
): GridColumns<ReportsNormalizedReportsDatum> => {
  const conditionalColumn: GridColumn<ReportsNormalizedReportsDatum> = isStoreGroupReport
    ? {
        id: 'storeGroup' as const,
        width: 75,
        Cell: ({ row }: CellProps<ReportsNormalizedReportsDatum>) =>
          row.original.retailerLocationGroupName ?? '-',
      }
    : {
        id: 'banner' as const,
        width: 75,
        Cell: ({ row }: CellProps<ReportsNormalizedReportsDatum>) => row.original.retailerId ?? '-',
      }

  return [
    {
      id: 'name',
      width: 200,
    },
    {
      id: 'reportType',
      Cell: ({ row }) =>
        useDomainMessage(
          `reportsDomain.normalizedReports.list.reportTypes.${row.original.reportType}` as DomainMessages
        ),
    },
    {
      id: 'fileType',
      width: 75,
      Cell: ({ row }) => row.original.fileType.toUpperCase(),
    },
    conditionalColumn,
    {
      id: 'createdAt',
      width: 75,
      Cell: ({ row }) => <FormattedDate timeZone="utc" value={row.original.createdAt} />,
    },
    {
      id: 'tools',
      Header: '',
      Cell: ReportsNormalizedReportsListDownloadCell,
    },
  ]
}

export const ReportsNormalizedReportsGridList: FunctionComponent<
  React.PropsWithChildren<ReportsNormalizedReportsGridListProps>
> = ({ loading, error, reports, loadMore, canLoadMore, refetch, isHeavyQuery, reportType }) => {
  const messages = useDomainMessages({
    isHeavyQuery: 'reportsDomain.normalizedReports.list.isHeavyQuery',
    noResults: 'reportsDomain.normalizedReports.list.noResults',
  })

  const isStoreGroupReport = Boolean(
    reportType && REPORT_TYPES_BASED_ON_STORE_GROUPS.includes(reportType)
  )

  const columns = useMemo(() => {
    const baseColumns = getBaseColumns(isStoreGroupReport)
    return baseColumns.map(column => ({
      ...column,
      Cell: column.Cell ?? ReportsNormalizedReportsListDefaultCell,
      Header: column.Header ?? ReportsNormalizedReportsListDefaultHeader,
    })) as GridColumns<ReportsNormalizedReportsDatum>
  }, [isStoreGroupReport])

  if (error) return <ErrorPage action={refetch} />

  return (
    <ExpandableGridList
      onLoadMore={loadMore}
      data={reports || []}
      columns={columns as GridColumns<GridDatum>}
      loadingMore={loading}
      canLoadMore={canLoadMore ?? false}
      emptyState={
        <div css={{ marginTop: '200px' }}>
          <EmptyState>
            <EmptyStateArtwork src={reportsEmptyStateImg} />
            <EmptyStateTitle>
              {isHeavyQuery ? messages.isHeavyQuery : messages.noResults}
            </EmptyStateTitle>
          </EmptyState>
        </div>
      }
    />
  )
}
